import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  Badge,
  Group,
  Text,
  Pagination,
  LoadingOverlay,
  Select,
  Tabs,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Link, useNavigate } from "react-router-dom";
import {
  mvpApiListJobsInFlight,
  mvpApiListJobs,
  JobStatus,
  JobType,
} from "@augmodo/lebron-browser-client";
import {
  mvpApiListStores,
  type StoreSchema as AtlasStoreSchema,
} from "@labeling-tools/api/atlas";
import { IconAdjustments, IconCalendar } from "@tabler/icons-react";
import { useGate } from "statsig-react";
import {
  Job,
  getStatusColor,
  getStatusText,
} from "@labeling-tools/ui-components/src/utils/helpers";

export const JobsTable: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<string>("product-association");
  const [associationJobs, setAssociationJobs] = useState<Job[]>([]);
  const [associationJobsAdmin, setAssociationJobsAdmin] = useState<Job[]>([]);
  const [qaJobs, setQaJobs] = useState<Job[]>([]);
  const [qaJobCount, setQaJobCount] = useState<number>(0);
  const [associationJobCount, setAssociationJobCount] = useState<number>(0);
  const [associationJobsAdminCount, setAssociationJobsAdminCount] =
    useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [currentJobListResponse, setCurrentJobListResponse] =
    useState<any>(null);
  const [stores, setStores] = useState<AtlasStoreSchema[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null); // eslint-disable-line
  const navigate = useNavigate();

  const { value: isAdmin, isLoading: statsigLoading } =
    useGate("out_of_stock_admin");

  const pageSize = 100;
  const msPolling = 20000; // 10 seconds

  // Get current jobs based on active tab
  const getCurrentJobs = () => {
    switch (activeTab) {
      case "admin-product-association":
        return associationJobsAdmin;
      case "qa-jobs":
        return qaJobs;
      default: // standard jobs
        return associationJobs;
    }
  };

  const currentJobs = getCurrentJobs();

  // Fetch jobs by a specific job type
  const fetchByJobType = useCallback(
    async (
      page: number,
      storeId: string | null,
      dateRange: [Date | null, Date | null] = [null, null],
      type: string = "AAD_REVIEW_v2",
    ) => {
      const queryParams: any = {
        // type: JobType.AAD_REVIEW_v2, TOOD: uncomment this
        type,
        page_size: pageSize,
        page: page,
      };

      // Add date range filters if provided (admin only)
      if (isAdmin && dateRange[0] && dateRange[1]) {
        queryParams.created_after = dateRange[0].toISOString();
        queryParams.created_before = dateRange[1].toISOString();
      } else if (!isAdmin) {
        queryParams.created_after = "2025-01-01T00:00:00Z"; // TODO: make this dynamic
      }

      // Add store filter if selected
      if (storeId) {
        queryParams.store_id = storeId;
      }

      const response = await mvpApiListJobsInFlight({
        query: queryParams,
        throwOnError: true,
      });

      const listJobsResponse = response.data;

      // Transform API jobs to our Job interface
      const transformedJobs = listJobsResponse?.jobs?.map((apiJob: any) => ({
        id: apiJob.id,
        store: apiJob.store,
        status: apiJob.status || JobStatus.NEEDS_REVIEW,
        created_at: apiJob.created_at || new Date().toISOString(),
      }));

      return {
        jobs: transformedJobs,
        count: listJobsResponse.count,
        total_pages: listJobsResponse.total_pages,
        listJobsResponse,
      };
    },
    [pageSize, isAdmin],
  );

  const fetchJobsOfAllStatus = useCallback(
    async (
      page: number,
      storeId: string | null,
      dateRange: [Date | null, Date | null] = [null, null],
    ) => {
      if (!isAdmin) return null;
      const queryParams: any = {
        page_size: pageSize,
        page: page,
        type: JobType.AAD_REVIEW_V2,
        status: [
          JobStatus.NEEDS_REVIEW,
          JobStatus.IN_PROGRESS,
          JobStatus.COMPLETED,
        ],
      };
      // default date range to 7 days ago
      if (dateRange[0] && dateRange[1]) {
        queryParams.created_after = dateRange[0].toISOString();
        queryParams.created_before = dateRange[1].toISOString();
      }

      // Add store filter if selected
      if (storeId) {
        queryParams.store_id = storeId;
      }

      const response = await mvpApiListJobs({
        query: queryParams,
        throwOnError: true,
      });

      const listJobsResponse = response.data;

      // Transform API jobs to our Job interface
      const transformedJobs = listJobsResponse.jobs.map((apiJob: any) => ({
        id: apiJob.id,
        store: apiJob.store,
        status: apiJob.status,
        created_at: apiJob.created_at || new Date().toISOString(),
      }));

      return {
        jobs: transformedJobs,
        count: listJobsResponse.count,
        total_pages: listJobsResponse.total_pages,
        listJobsResponse,
      };
    },
    [pageSize, isAdmin],
  );

  // Fetch both job types simultaneously
  const fetchAllJobs = useCallback(
    async (
      page: number,
      storeId: string | null,
      dateRange: [Date | null, Date | null] = [null, null],
    ) => {
      try {
        setLoading(true);

        // Fetch both job types in parallel
        const results = await Promise.allSettled([
          fetchByJobType(page, storeId, dateRange),
          fetchByJobType(page, storeId, dateRange, "AAD_REVIEW_v2_QA"),
          fetchJobsOfAllStatus(page, storeId, dateRange),
        ]);

        // Extract results, handling failures gracefully
        const associationResult =
          results[0].status === "fulfilled" ? results[0].value : null;
        const qaResult =
          results[1].status === "fulfilled" ? results[1].value : null;
        const associationResultAdmin =
          results[2].status === "fulfilled" ? results[2].value : null;

        // Update association jobs state
        setAssociationJobs(associationResult?.jobs ?? []);
        setAssociationJobCount(associationResult?.count ?? 0);

        // Update QA jobs state
        if (qaResult?.jobs) {
          setQaJobs(qaResult?.jobs ?? []);
          setQaJobCount(qaResult?.count ?? 0);
        }

        // Update admin job states (only if admin and results exist)
        if (associationResultAdmin?.jobs) {
          setAssociationJobsAdmin(associationResultAdmin.jobs);
          setAssociationJobsAdminCount(associationResultAdmin?.count ?? 0);
        }

        // Set the current job list request based on active tab
        if (activeTab === "product-association" && associationResult) {
          setCurrentJobListResponse(associationResult);
        } else if (activeTab === "qa-jobs" && qaResult) {
          setCurrentJobListResponse(qaResult);
        } else if (
          activeTab === "admin-product-association" &&
          associationResultAdmin
        ) {
          setCurrentJobListResponse(associationResultAdmin);
        }

        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error("Failed to fetch jobs:", e);
      }
    },
    [fetchByJobType, fetchJobsOfAllStatus, activeTab],
  );

  // Fetch stores for filter dropdown
  const fetchStores = useCallback(async () => {
    try {
      const storesResponse = await mvpApiListStores({
        query: {
          page_size: 1000,
          page: 1,
        },
        throwOnError: true,
      });

      setStores(storesResponse.data.stores);
    } catch (e) {
      console.error("Failed to fetch stores:", e);
    }
  }, []);

  const setupPolling = useCallback(() => {
    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Set up new polling interval - only fetch current page
    pollIntervalRef.current = setInterval(() => {
      fetchAllJobs(currentPage, selectedStoreId, dateRange);
    }, msPolling);
  }, [fetchAllJobs, currentPage, msPolling, selectedStoreId, dateRange]);

  // Initial load
  useEffect(() => {
    // For admin users, wait until date range is set before initial load
    if (
      statsigLoading ||
      (isAdmin && dateRange[0] === null && dateRange[1] === null)
    ) {
      return; // Skip initial load, will load after date range is set
    }
    fetchAllJobs(currentPage, null, dateRange);
  }, [fetchAllJobs, dateRange, isAdmin, statsigLoading]);

  // Set default date range for admin users
  useEffect(() => {
    if (isAdmin && dateRange[0] === null && dateRange[1] === null) {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 1);
      // Set end date to end of today to include jobs created today
      endDate.setHours(23, 59, 59, 999);
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 7);
      // Set start date to beginning of the day 7 days ago
      startDate.setHours(0, 0, 0, 0);
      setDateRange([startDate, endDate]);
      // The initial load effect will trigger after dateRange is updated
    }
  }, [isAdmin]);

  // Setup polling
  useEffect(() => {
    setupPolling();

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [setupPolling]);

  // Fetch stores
  useEffect(() => {
    fetchStores();
  }, []);

  const totalPages = currentJobListResponse?.total_pages || 1;

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchAllJobs(page, selectedStoreId, dateRange);
    },
    [fetchAllJobs, dateRange],
  );

  const handleStoreFilterChange = useCallback(
    (storeId: string | null) => {
      setSelectedStoreId(storeId);
      setCurrentPage(1); // Reset to first page when filter changes
      fetchAllJobs(1, storeId, dateRange);
    },
    [fetchAllJobs, dateRange],
  );

  const handleDateRangeChange = useCallback(
    (value: [Date | null, Date | null]) => {
      setDateRange(value);

      // Only refetch if both start and end dates are selected, or if clearing the range
      if ((value[0] && value[1]) || (!value[0] && !value[1])) {
        setCurrentPage(1); // Reset to first page when filter changes
        fetchAllJobs(1, selectedStoreId, value);
      }
    },
    [fetchAllJobs, selectedStoreId],
  );

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (value) {
        setActiveTab(value);
        setCurrentPage(1); // Reset to first page when tab changes

        // Update current job list request based on new active tab
        let currentJobs;
        let currentCount;

        switch (value) {
          case "admin-product-association":
            currentJobs = associationJobsAdmin;
            currentCount = associationJobsAdminCount;
            break;
          case "qa-jobs":
            currentJobs = qaJobs;
            currentCount = qaJobCount;
            break;
          default: // standard jobs
            currentJobs = associationJobs;
            currentCount = associationJobCount;
            break;
        }

        if (currentJobs.length > 0) {
          // We have data, so we can update the request info immediately
          setCurrentJobListResponse({
            jobs: currentJobs,
            count: currentCount,
            total_pages: Math.ceil(currentCount / pageSize),
          });
        }
      }
    },
    [
      associationJobs,
      associationJobsAdmin,
      associationJobCount,
      qaJobCount,
      associationJobsAdminCount,
      qaJobs,
      pageSize,
    ],
  );

  const handleRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, jobId: string) => {
      const to = `/jobs/${jobId}/${activeTab}`;
      if (e.metaKey || e.ctrlKey) {
        window.open(to, "_blank");
      } else {
        navigate(to);
      }
    },
    [navigate, activeTab],
  );

  const rows = currentJobs.map((job) => (
    <Table.Tr
      key={job.id}
      onClick={(e) => handleRowClick(e, job.id)}
      style={{ cursor: "pointer" }}
      className="hover:bg-gray-50 transition-colors"
    >
      <Table.Td>
        <Group gap="xs">
          <Text fw={600} size="sm">
            <Link
              to={`/jobs/${job.id}/${activeTab}`}
              style={{ textDecoration: "underline" }}
              onClick={(e) => e.stopPropagation()}
            >
              {job.id}
            </Link>
          </Text>
          <Text c="dimmed" size="sm">
            {job.store?.name || "Unknown Store"}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge color={getStatusColor(job.status)} variant="light" size="sm">
          {getStatusText(job.status)}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{new Date(job.created_at).toLocaleDateString()}</Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div className="space-y-6 w-full h-full p-6">
      {/* Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="max-w-2xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <IconAdjustments />
              <Text size="md" fw={600} c="gray.8">
                Filters
              </Text>
            </div>
          </div>

          <div className="space-y-3 flex flex-row gap-4 items-center">
            <div className="space-y-1 text-left">
              <Text size="sm" fw={500} c="gray.7" className="text-left">
                Store
              </Text>
              <Select
                placeholder="Select a store to filter"
                value={selectedStoreId}
                onChange={handleStoreFilterChange}
                data={stores.map((store) => ({
                  value: store.lebron_id || store.id,
                  label: store.name,
                }))}
                clearable
                size="sm"
                searchable
                w={300}
                styles={{
                  input: {
                    borderColor: "#e2e8f0",
                    "&:focus": {
                      borderColor: "#3b82f6",
                    },
                  },
                }}
              />
            </div>
            {isAdmin && (
              <div className="space-y-1 text-left mt-0 !mt-0">
                <Text size="sm" fw={500} c="gray.7" className="text-left">
                  Date Range
                </Text>
                <DatePickerInput
                  type="range"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  leftSection={<IconCalendar size={16} />}
                  clearable
                  size="sm"
                  w={300}
                  valueFormat="MMM DD, YYYY"
                  styles={{
                    input: {
                      borderColor: "#e2e8f0",
                      "&:focus": {
                        borderColor: "#3b82f6",
                      },
                    },
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm w-full h-full relative flex flex-col">
        <LoadingOverlay visible={loading} />

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Jobs</h2>
        </div>

        <Tabs value={activeTab} onChange={handleTabChange} className="mb-4">
          <Tabs.List>
            <Tabs.Tab value="product-association">
              Product Association Jobs{" "}
              {associationJobCount > 0 && `(${associationJobCount})`}
            </Tabs.Tab>
            {isAdmin && (
              <>
                <Tabs.Tab value="qa-jobs">
                  QA Jobs {qaJobCount > 0 && `(${qaJobCount})`}
                </Tabs.Tab>
                <Tabs.Tab value="admin-product-association">
                  Admin View
                </Tabs.Tab>
              </>
            )}
          </Tabs.List>
        </Tabs>

        {/* empty state */}
        {!rows.length && !loading && (
          <div className="flex-1 min-h-0">
            <Text>No jobs found</Text>
          </div>
        )}

        {rows.length > 0 && (
          <div className="flex-1 min-h-0">
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Job ID / Store</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>Status</Table.Th>
                  <Table.Th style={{ textAlign: "center" }}>
                    Created Date
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </div>
        )}

        <div className="flex justify-center mt-6">
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={handlePageChange}
            size="sm"
          />
        </div>
      </div>
    </div>
  );
};
